import { Check, Loader2, X, CircleAlert } from "lucide-react";
import { LETTERS, SKILL_LABELS } from "@/components/test-section/meta";
import FigureView from "@/components/test-section/FigureView";
import HighlightableText from "@/components/HighlightableText";
import { StrikeButton, struckStyle } from "@/components/test-section/ExamTools";

const STEPS = [
  "Selecting learning objectives",
  "Selecting reasoning skills",
  "Writing the scientific scenario",
  "Writing 5–6 questions",
  "Independently verifying every answer",
  "Checking question count",
  "Publishing (or rejecting & rewriting)",
];

export function BuildingView({ session }) {
  return (
    <div className="max-w-2xl mx-auto py-10" data-testid="sp-building">
      <h1 className="font-display text-3xl font-bold mb-2">Building your passages…</h1>
      <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
        New passages take about 2–6 minutes because each one is validated before you see it. You can leave and come back — it keeps building.
      </p>
      <div className="space-y-2 mb-6">
        {session.slots.map((s) => (
          <div key={s.idx} className="pm-card p-4 flex items-center gap-3" data-testid={`sp-slot-${s.idx}`}>
            {s.status === "ready" ? <Check size={16} style={{ color: "var(--mastery)" }} />
              : s.status === "failed" ? <X size={16} style={{ color: "var(--weakness)" }} />
                : <Loader2 size={16} className="animate-spin" style={{ color: "var(--primary)" }} />}
            <span className="font-medium">Passage {s.idx + 1}</span>
            <span className="text-xs ml-auto" style={{ color: "var(--text-muted)" }}>
              {s.status === "ready" ? (s.source === "bank" ? "ready · from shared bank" : "ready · freshly validated")
                : s.status === "failed" ? (s.fail_reason === "ai_busy" ? "AI writer at capacity · credit refunded" : "couldn't pass checks in time · credit refunded") : `writing${s.topic ? ` · ${s.topic}` : ""}`}
            </span>
          </div>
        ))}
      </div>
      <ol className="text-xs space-y-1.5" style={{ color: "var(--text-muted)" }}>
        {STEPS.map((t, i) => <li key={t}><span className="font-mono mr-2">{i + 1}.</span>{t}</li>)}
      </ol>
    </div>
  );
}

export function PassagePane({ passage, highlightKey }) {
  const t = passage.tags;
  return (
    <div data-testid="sp-passage">
      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className="pm-badge" style={{ background: "var(--hl-blue)" }} data-testid="sp-passage-type">Type {passage.passage_type} · {passage.passage_type_name}</span>
        <span className="pm-badge" style={{ background: "var(--muted)" }} data-testid="sp-tag-foundation">{t.foundation_code}</span>
        <span className="pm-badge" style={{ background: "var(--muted)" }} data-testid="sp-tag-category">{t.category_code}</span>
      </div>
      <h2 className="font-display text-xl font-bold mb-1" data-testid="sp-passage-title">{passage.title}</h2>
      <div className="text-xs mb-4" style={{ color: "var(--text-muted)" }} data-testid="sp-tag-topic">{t.topic}{t.subtopic ? ` › ${t.subtopic}` : ""}</div>
      {highlightKey ? (
        <div className="leading-relaxed text-[15px] pt-8">
          <HighlightableText text={passage.paragraphs.join("\n\n")} storageKey={highlightKey} testid="sp-passage-highlights" />
        </div>
      ) : passage.paragraphs.map((p, i) => <p key={i} className="mb-3 leading-relaxed text-[15px]">{p}</p>)}
      <FigureView figures={passage.figures} idPrefix="sp-figure" />
    </div>
  );
}

export function QuestionPane({ q, number, answer, onChoose, review, struck = [], onStrike }) {
  const chosen = answer?.choice;
  return (
    <div data-testid={`sp-question-${number}`}>
      <div className="flex items-center gap-2 mb-2 text-xs" style={{ color: "var(--text-muted)" }}>
        <span className="font-mono">Question {number}</span>
        {review && <span className="pm-badge" style={{ background: "var(--muted)" }} data-testid={`sp-q-skill-${number}`}>{SKILL_LABELS[q.skill] || q.skill}</span>}
        {review && q.objective && <span className="truncate" data-testid={`sp-q-objective-${number}`}>Objective: {q.objective}</span>}
      </div>
      <p className="font-medium mb-4 leading-relaxed" data-testid="sp-question-stem">{q.stem}</p>
      <div className="space-y-2">
        {q.choices.map((c, i) => {
          const isRight = review && i === q.answer_idx;
          const isWrongPick = review && chosen === i && i !== q.answer_idx;
          const bg = isRight ? "var(--hl-green)" : isWrongPick ? "rgba(239,68,68,0.12)" : chosen === i ? "var(--hl-blue)" : "var(--paper)";
          const border = isRight ? "var(--mastery)" : isWrongPick ? "var(--weakness)" : chosen === i ? "var(--primary)" : "var(--border)";
          return (
            <div key={i}>
              <div className="flex gap-2">
                <button disabled={review} onClick={() => onChoose(i)}
                  className="w-full text-left rounded-xl px-4 py-3 flex gap-3 transition-colors disabled:cursor-default"
                  style={{ background: bg, border: `1.5px solid ${border}` }} data-testid={`sp-choice-${number}-${LETTERS[i]}`}>
                  <span className="font-mono font-bold">{LETTERS[i]}.</span>
                  <span className="flex-1" style={!review && struck.includes(i) ? struckStyle : undefined}>{c}</span>
                  {isRight && <Check size={16} style={{ color: "var(--mastery)" }} />}
                  {isWrongPick && <X size={16} style={{ color: "var(--weakness)" }} />}
                </button>
                {!review && onStrike && <StrikeButton on={struck.includes(i)} onClick={() => onStrike(i)} testid={`sp-strike-${number}-${LETTERS[i]}`} />}
              </div>
              {review && q.misconceptions?.[i] && (
                <p className="text-xs ml-9 mt-1.5 px-2.5 py-1 rounded-lg inline-block" style={{ background: "rgba(239,68,68,0.08)", color: "var(--weakness)" }} data-testid={`sp-misconception-${number}-${LETTERS[i]}`}>
                  <b>Misconception:</b> {q.misconceptions[i]}
                </p>
              )}
              {review && q.explanations && (
                <p className="text-xs mt-1 mb-2 ml-9 leading-relaxed" style={{ color: "var(--text-secondary)" }} data-testid={`sp-expl-${number}-${LETTERS[i]}`}>{q.explanations[i]}</p>
              )}
            </div>
          );
        })}
      </div>
      {review && chosen == null && (
        <p className="text-xs mt-2 inline-flex items-center gap-1" style={{ color: "var(--weakness)" }}><CircleAlert size={12} /> Not answered</p>
      )}
    </div>
  );
}

export function Palette({ items, current, answers, onJump }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="sp-palette">
      {items.map((it, i) => {
        const a = answers[it.key] || {};
        const on = i === current;
        return (
          <button key={it.key} onClick={() => onJump(i)}
            className="w-8 h-8 rounded-lg text-xs font-mono relative"
            style={{ background: on ? "var(--primary)" : a.choice != null ? "var(--hl-blue)" : "var(--paper)", color: on ? "white" : "var(--text)", border: `1.5px solid ${on ? "var(--primary)" : "var(--border)"}` }}
            data-testid={`sp-palette-${i + 1}`}>
            {i + 1}
            {a.flagged && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full" style={{ background: "#e08a2a" }} />}
          </button>
        );
      })}
    </div>
  );
}
